import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Avatar, Button, Chip, IconButton, InputAdornment, LinearProgress, TextField, Tooltip
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import styles from "../VResume.module.css";
import VResumeEditor from "../VResumeEditor";
import {
    VRESUME_STATUS_COLORS, exportVResumePdf, listVResumes, openEditor
} from "../vresumeSlice";

/**
 * V-Resume — mobile.
 *
 * ── What is different, and why ────────────────────────────────────────────────────────
 *
 * The listing becomes cards instead of a DataGrid: a five-column grid on a 360px screen is
 * a horizontal scroll, and a manager checking on a phone wants to know who is behind, not
 * to read a table.
 *
 * The editor, however, is the SAME component as desktop, not a mobile twin. Its layout is
 * already a single column below 1400px (`.editorLayout` collapses the preview under the
 * form), and a CV has a lot of fields — a second implementation of all of them would drift
 * from the first, and the fields are the part that must not disagree. Editing a full CV on a
 * phone is not the primary use, but it works, and the preview is there to scroll to.
 */
export default function MobileVResumeView() {

    const dispatch = useDispatch()

    const list = useSelector((state) => state.vresume.list)
    const reach = useSelector((state) => state.vresume.reach)
    const editingCollabId = useSelector((state) => state.vresume.editingCollabId)
    const lang = useSelector((state) => state.settings.lang)
    const userId = useSelector((state) => state.login.userId)

    const [query, setQuery] = useState("")

    useEffect(() => {
        if (!editingCollabId) dispatch(listVResumes({}))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingCollabId])

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase()
        if (!needle) return list || []
        return (list || []).filter(row =>
            [row.nom, row.prenom, row.email, row.headline, row.teams]
                .filter(Boolean)
                .some(field => String(field).toLowerCase().includes(needle)))
    }, [list, query])

    const onExport = async (row) => {
        const name = [row.prenom, row.nom].filter(Boolean).join('_')
            .replace(/[^A-Za-z0-9_-]/g, '') || 'CV'
        const response = await dispatch(exportVResumePdf({
            idCollab: row.id_collab == userId ? undefined : row.id_collab,
            filename: `CV_${name}.pdf`
        }))
        if (response.payload && response.payload.responseError) {
            toast.error(Localize("VRESUME_EXPORT_FAILED", lang))
        }
    }

    if (editingCollabId) {
        return (
            <div className={`${styles.mobileContainer} ${styles.vresume}`}>
                <VResumeEditor />
            </div>
        )
    }

    return (
        <div className={`${styles.mobileContainer} ${styles.vresume}`}>

            <div className={styles.pageHeader} style={{ marginBottom: 12 }}>
                <h2 className={styles.pageTitle} style={{ fontSize: 17 }}>
                    <Langs str="VRESUME_TITLE" />
                </h2>
            </div>

            <Button fullWidth variant="contained" size="small"
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => dispatch(openEditor(userId))}
                sx={{
                    mb: 1.5, textTransform: 'none', fontFamily: 'roboto', fontSize: 13,
                    backgroundColor: 'var(--orange)',
                    '&:hover': { backgroundColor: '#e07f0d' }
                }}>
                <Langs str="VRESUME_OPEN_MINE" />
            </Button>

            {/* The search box only earns its space when the list holds other people. */}
            {reach.viewAll || reach.hasTeamReach
                ? <TextField
                    fullWidth size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={Localize("VRESUME_SEARCH_PLACEHOLDER", lang)}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                        sx: { fontFamily: 'roboto', fontSize: 13 },
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: '#a0a5b1' }} />
                            </InputAdornment>
                        )
                    }}
                />
                : null}

            {filtered.length === 0
                ? <div className={styles.emptyRows}><Langs str="VRESUME_NO_ROWS" /></div>
                : filtered.map(row => {
                    const photoUrl = NetworkManager.getInstance()
                        .getCollabPhotoUrl(row.id_collab, row.photo_updated_at)
                    const initials = [row.prenom, row.nom].filter(Boolean)
                        .map(part => part.charAt(0).toUpperCase()).join('')
                    const status = row.status || 'none'
                    const completion = row.completion || 0
                    return (
                        <div className={styles.mobileCard} key={row.id_collab}>
                            <div className={styles.mobileCardHead}>
                                <Avatar src={photoUrl || undefined}
                                    sx={{ width: 34, height: 34, fontSize: 13, bgcolor: 'var(--lightGrey)', color: '#6f6f6f' }}>
                                    {initials}
                                </Avatar>
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <div className={styles.personName}>
                                        {[row.prenom, row.nom].filter(Boolean).join(' ')}
                                    </div>
                                    <div className={styles.personSub}>
                                        {row.headline || row.teams || row.email}
                                    </div>
                                </div>
                                <Chip size="small"
                                    label={Localize(`VRESUME_STATUS_${status.toUpperCase()}`, lang)}
                                    sx={{
                                        ...(VRESUME_STATUS_COLORS[status] || VRESUME_STATUS_COLORS.none),
                                        '& .MuiChip-label': { fontFamily: 'roboto', fontSize: 10.5 }
                                    }} />
                            </div>

                            <LinearProgress
                                variant="determinate" value={completion}
                                sx={{
                                    height: 5, borderRadius: 3, mb: 0.75,
                                    backgroundColor: 'var(--lightGrey)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        backgroundColor: completion >= 100 ? 'var(--green)' : 'var(--orange)'
                                    }
                                }}
                            />

                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span className={styles.personSub} style={{ flex: '1 1 auto' }}>
                                    {completion}%
                                </span>
                                <Tooltip title={Localize(row.canEdit
                                    ? "VRESUME_OPEN_EDIT" : "VRESUME_OPEN_VIEW", lang)} arrow>
                                    <IconButton size="small"
                                        onClick={() => dispatch(openEditor(row.id_collab))}>
                                        {row.canEdit
                                            ? <EditOutlinedIcon sx={{ fontSize: 19, color: 'var(--vital-blue)' }} />
                                            : <VisibilityOutlinedIcon sx={{ fontSize: 19, color: '#6f6f6f' }} />}
                                    </IconButton>
                                </Tooltip>
                                {status !== 'none'
                                    ? <IconButton size="small" onClick={() => onExport(row)}>
                                        <PictureAsPdfOutlinedIcon sx={{ fontSize: 19, color: 'var(--orange)' }} />
                                    </IconButton>
                                    : null}
                            </div>
                        </div>
                    )
                })}
        </div>
    )
}
